import { InternalRefetchQueriesInclude, useApolloClient } from "@apollo/client";
import { filterNonNull, throwIfNil } from "@msys/common";
import { MenuButton, ModalOpenButton, SubMenuItem } from "@msys/ui";
import { Divider, Icon, MenuItem, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useParams } from "react-router-dom";
import { PermissionName } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import SapLogo from "../../assets/icons/sap-logo.svg?react";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { useOpenFile } from "../../commons/hooks/useOpenFile.js";
import {
  QuoteItemSapS4HanaData_QuotePreviewDocument,
  QuoteItemSapS4HanaData_QuotePreviewQueryVariables,
} from "../quotes/QuotePreview.generated.js";
import {
  S4HanaCancelReasonSelectProcess,
  S4HanaCancelReasonSelectProcessRef,
} from "./S4HanaCancelReasonSelectProcess.js";
import { S4HanaDocumentFlowModal } from "./S4HanaDocumentFlowModal.js";
import { S4HanaGetQuoteAsSalesQuotationMenuButton } from "./S4HanaGetQuoteAsSalesQuotationMenuButton.js";
import { S4HanaGetSalesQuotationFromRemoteMenuButton } from "./S4HanaGetSalesQuotationFromRemoteMenuButton.js";
import { S4HanaQuoteCopyModal } from "./S4HanaQuoteCopyModal.js";
import {
  S4HanaQuoteDataBoxDocument,
  S4HanaQuoteDataBoxQueryVariables,
} from "./S4HanaQuoteDataBox.generated.js";
import {
  S4HanaQuoteItemDataBoxDocument,
  S4HanaQuoteItemDataBoxQueryVariables,
} from "./S4HanaQuoteItemDataBox.generated.js";
import {
  S4HanaQuoteDataDocument,
  S4HanaQuoteDataQueryVariables,
  useS4HanaCancelQuoteMutation,
  useS4HanaCreateQuoteMutation,
  useS4HanaQuoteDataQuery,
  useS4HanaSimulateQuotationMutation,
  useS4HanaUncancelQuoteMutation,
  useS4HanaUpdateQuoteMutation,
  useSapS4HanaExportQuoteAsMutation,
  useSapS4HanaUpdateQuoteFromRemoteMutation,
} from "./S4HanaQuoteMenuButton.generated.js";
import { makeRefetchQuery } from "../../../clients/apollo-client-refetch-queries.js";
import {
  S4HanaQuoteItemDataHooksDocument,
  S4HanaQuoteItemDataHooksQueryVariables,
} from "./hooks.generated.js";
import {
  RfItemAttachmentsBoxDocument,
  RfItemAttachmentsBoxQueryVariables,
} from "./richter-frenzel/RfQuoteItemAttachmentsBox.generated.js";

interface Props {
  projectId: string;
  quoteId: string;
  expandedItemIds: string[] | undefined;
  docViewerPermissions: PermissionName[];
}

export const S4HanaQuoteMenuButton = ({
  projectId,
  quoteId,
  expandedItemIds,
  docViewerPermissions,
}: Props) => {
  const { t } = useTranslate(["SapS4Hana", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const isDebugOutputEnabled = useFeature("DebugOutput");

  const cancelReasonSelectProcessRef =
    React.useRef<S4HanaCancelReasonSelectProcessRef>(null);

  const { itemId } = useParams();

  // TODO: how can we specify this in a more slim way? We could use typedDocumentNode but the generation would mean bigger bundle size
  const refetchQueries = React.useMemo<InternalRefetchQueriesInclude>(() => {
    return filterNonNull<InternalRefetchQueriesInclude[number]>([
      makeRefetchQuery<S4HanaQuoteDataQueryVariables>({
        document: S4HanaQuoteDataDocument,
        variables: {
          projectId,
          quoteId,
        },
      }),
      itemId
        ? makeRefetchQuery<S4HanaQuoteItemDataBoxQueryVariables>({
            document: S4HanaQuoteItemDataBoxDocument,
            variables: { projectId, quoteId, itemId },
          })
        : null,
      itemId
        ? makeRefetchQuery<S4HanaQuoteItemDataHooksQueryVariables>({
            document: S4HanaQuoteItemDataHooksDocument,
            variables: { projectId, quoteId, itemId },
          })
        : null,
      itemId
        ? makeRefetchQuery<RfItemAttachmentsBoxQueryVariables>({
            document: RfItemAttachmentsBoxDocument,
            variables: { projectId, docId: quoteId, itemId },
          })
        : null,
      makeRefetchQuery<S4HanaQuoteDataBoxQueryVariables>({
        document: S4HanaQuoteDataBoxDocument,
        variables: {
          projectId,
          quoteId,
        },
      }),
      makeRefetchQuery<QuoteItemSapS4HanaData_QuotePreviewQueryVariables>({
        document: QuoteItemSapS4HanaData_QuotePreviewDocument,
        variables: { projectId, docId: quoteId },
      }),
    ]);
  }, [projectId, quoteId, itemId]);

  const client = useApolloClient();
  const query = useS4HanaQuoteDataQuery({
    client,
    variables: { projectId, quoteId },
  });
  const [simulateQuotation, { loading: simulateQuotationIsLoading }] =
    useS4HanaSimulateQuotationMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [createQuote, { loading: createQuoteIsLoading }] =
    useS4HanaCreateQuoteMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [updateQuote, { loading: updateQuoteIsLoading }] =
    useS4HanaUpdateQuoteMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [cancelQuote, { loading: cancelQuoteIsLoading }] =
    useS4HanaCancelQuoteMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [uncancelQuote, { loading: uncancelQuoteIsLoading }] =
    useS4HanaUncancelQuoteMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const [updateQuoteFromRemote, { loading: updateQuoteFromRemoteLoading }] =
    useSapS4HanaUpdateQuoteFromRemoteMutation({
      client,
      variables: { input: { projectId, docId: quoteId, expandedItemIds } },
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const [sapS4HanaExportQuoteAs, { loading: sapS4HanaExportQuoteAsLoading }] =
    useSapS4HanaExportQuoteAsMutation({ client });

  const { openPdf } = useOpenFile();

  const availableCancelReasons =
    query.data?.quoteSapS4HanaData
      ?.xAvailableSalesQuotationItemSalesDocumentRejectionReasons;
  const defaultCancelReason =
    query.data?.quoteSapS4HanaData
      ?.xSalesQuotationItemSalesDocumentRejectionReasonDefault;
  const crmRedirectUrl =
    query.data?.quoteSapS4HanaData?.salesQuotationCrmRedirectUrl;
  const fioriCalculationAppUrl =
    query.data?.quoteSapS4HanaData?.salesQuotationFioriCalculatorUrl;
  const availableDocumentExportTypes =
    query.data?.quoteSapS4HanaData?.availableDocumentExportTypes ?? [];

  const salesQuotationId = query.data?.quoteSapS4HanaData?.xId;
  const overallRejectionStatus =
    query.data?.quoteSapS4HanaData?.xOverallSdDocumentRejectionSts;

  const menuItems = [
    <MenuItem
      key="quotation-simutation"
      onClick={async () => {
        await simulateQuotation();
      }}
      disabled={simulateQuotationIsLoading}
    >
      {t("SAP quotation simulation", { ns: "SapS4Hana" })}
    </MenuItem>,
    ...(!query.loading && query.data?.quoteSapS4HanaData?.xId
      ? [
          <ModalOpenButton
            Modal={S4HanaDocumentFlowModal}
            modalProps={{
              projectId,
              docId: quoteId,
              salesQuotationId: query.data.quoteSapS4HanaData.xId,
            }}
            key="document-flow-sap-quote"
          >
            <MenuItem>{t("Show document flow", { ns: "SapS4Hana" })}</MenuItem>
          </ModalOpenButton>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="copy-sap-quote"
          >
            <ModalOpenButton
              Modal={S4HanaQuoteCopyModal}
              modalProps={{ projectId, docId: quoteId }}
            >
              <MenuItem>
                {t("Copy with reference", { ns: "SapS4Hana" })}
              </MenuItem>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="update-sap-quote"
          >
            <MenuItem
              onClick={async () => {
                const result = await updateQuote();

                if (
                  result.data?.updateSapS4HanaSalesQuoteFromQuote?.errorMessage
                ) {
                  enqueueSnackbar(
                    result.data.updateSapS4HanaSalesQuoteFromQuote.errorMessage,
                    {
                      variant: "error",
                    }
                  );
                } else {
                  enqueueSnackbar(t("Quote updated", { ns: "SapS4Hana" }), {
                    variant: "success",
                  });
                }
              }}
              disabled={updateQuoteIsLoading}
            >
              {t("Update SAP quote", { ns: "SapS4Hana" })}
            </MenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="cancel-sap-quote"
          >
            <MenuItem
              onClick={async () => {
                const cancelReasonCode =
                  await cancelReasonSelectProcessRef.current?.startProcess({
                    availableReasons: availableCancelReasons ?? [],
                    defaultReason: defaultCancelReason || null,
                    isReasonRequired: true,
                  });
                if (!cancelReasonCode) return;
                await cancelQuote({
                  variables: {
                    input: {
                      projectId,
                      docId: quoteId,
                      reason: cancelReasonCode,
                      expandedItemIds,
                    },
                  },
                  refetchQueries,
                  awaitRefetchQueries: true,
                });
                enqueueSnackbar(t("Quote cancelled", { ns: "SapS4Hana" }));
              }}
              // disabled when already fully rejected (C) — nothing left to cancel
              disabled={cancelQuoteIsLoading || overallRejectionStatus === "C"}
            >
              {t("Cancel SAP quote", { ns: "SapS4Hana" })}
              <S4HanaCancelReasonSelectProcess
                ref={cancelReasonSelectProcessRef}
              />
            </MenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="reinstate-sap-quote"
          >
            <MenuItem
              onClick={async () => {
                await uncancelQuote();
                enqueueSnackbar(t("Quote restored", { ns: "SapS4Hana" }), {
                  variant: "success",
                });
              }}
              // disabled when quote is open (A)
              disabled={
                uncancelQuoteIsLoading || overallRejectionStatus === "A"
              }
            >
              {t("Restore SAP quote", { ns: "SapS4Hana" })}
            </MenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="update-quote-from-remote"
          >
            <MenuItem
              onClick={async () => {
                await updateQuoteFromRemote();
                enqueueSnackbar(t("Quote updated", { ns: "SapS4Hana" }), {
                  variant: "success",
                });
              }}
              disabled={updateQuoteFromRemoteLoading}
            >
              {t("Update quote from remote", { ns: "SapS4Hana" })}
            </MenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="create-sap-pdf"
          >
            <SubMenuItem
              label={t("Export as", { ns: "SapS4Hana" })}
              disabled={availableDocumentExportTypes.length === 0}
            >
              {availableDocumentExportTypes.map(exportType => (
                <MenuItem
                  key={exportType.value}
                  onClick={async () => {
                    try {
                      const result = await sapS4HanaExportQuoteAs({
                        variables: {
                          input: {
                            docId: quoteId,
                            projectId,
                            exportType: exportType.value,
                          },
                        },
                      });

                      const file = throwIfNil(
                        result.data?.sapS4HanaGenerateQuoteExport?.file,
                        "Failed to export document"
                      );
                      openPdf(file.url, `Quote_${quoteId}`);
                    } catch (error) {
                      if (error instanceof Error) {
                        enqueueSnackbar(error.message, { variant: "error" });
                      }
                    }
                  }}
                  disabled={updateQuoteFromRemoteLoading}
                >
                  {`${exportType.value} - ${exportType.label}`}
                </MenuItem>
              ))}
            </SubMenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
          ...(crmRedirectUrl
            ? [
                <RestrictedByDocumentPermissionWithDebug
                  permission="MANAGE_QUOTES"
                  document={{ viewerPermissions: docViewerPermissions }}
                  key="open-in-crm-sap"
                >
                  <MenuItem href={crmRedirectUrl} target="_blank" component="a">
                    {t("Open in CRM", { ns: "SapS4Hana" })}
                  </MenuItem>
                </RestrictedByDocumentPermissionWithDebug>,
              ]
            : []),

          ...(fioriCalculationAppUrl
            ? [
                <MenuItem
                  key="open-in-fiori-calculation-app"
                  href={fioriCalculationAppUrl}
                  target="_blank"
                  component="a"
                >
                  {t("Calculate", { ns: "SapS4Hana" })}
                </MenuItem>,
              ]
            : []),
        ]
      : []),
    ...(!query.loading && !query.data?.quoteSapS4HanaData?.xId
      ? [
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions: docViewerPermissions }}
            key="create-sap-quote"
          >
            <MenuItem
              onClick={async () => {
                const result = await createQuote();

                if (
                  result.data?.createSapS4HanaSalesQuoteFromQuote?.errorMessage
                ) {
                  enqueueSnackbar(
                    result.data.createSapS4HanaSalesQuoteFromQuote.errorMessage,
                    {
                      variant: "error",
                    }
                  );
                } else {
                  enqueueSnackbar(t("Quote created", { ns: "SapS4Hana" }), {
                    variant: "success",
                  });
                }
              }}
              disabled={createQuoteIsLoading}
            >
              {t("Create SAP quote", { ns: "SapS4Hana" })}
            </MenuItem>
          </RestrictedByDocumentPermissionWithDebug>,
        ]
      : []),
    ...(isDebugOutputEnabled
      ? [
          <Divider key="divider-msys-debug" />,
          <S4HanaGetSalesQuotationFromRemoteMenuButton
            key="get-sales-quotation-from-remote"
            salesQuotationId={salesQuotationId ?? undefined}
            title={"Download remote sales quotation"}
          />,
          <S4HanaGetQuoteAsSalesQuotationMenuButton
            key={`get-quote-as-sales-quotation-${quoteId}-GET`}
            projectId={projectId}
            docId={quoteId}
            intent="GET"
            title={"Download local sales quotation"}
          />,
          <S4HanaGetQuoteAsSalesQuotationMenuButton
            key={`get-quote-as-sales-quotation-${quoteId}-CREATE`}
            projectId={projectId}
            docId={quoteId}
            intent="CREATE"
            title={"Download CREATE payload"}
          />,
          <S4HanaGetQuoteAsSalesQuotationMenuButton
            key={`get-quote-as-sales-quotation-${quoteId}-UPDATE`}
            projectId={projectId}
            docId={quoteId}
            intent="UPDATE"
            title={"Download UPDATE payload"}
          />,
        ]
      : []),
  ];

  return (
    <MenuButton
      Icon={
        <Tooltip title={t("SAP actions", { ns: "SapS4Hana" })}>
          <Icon>
            <SapLogo />
          </Icon>
        </Tooltip>
      }
    >
      {menuItems}
    </MenuButton>
  );
};
